/**
 * Patient Panel — pure formatting helpers (Phase 7).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE. The node-tests execute it verbatim (they strip `export ` and eval
 * the source), exactly like utils/scheduleView/layout.js, so a single `import` breaks the suite.
 *
 * ⛔ No `new Date()` on an Open Dental value. OD dates are the practice's wall-clock and the API
 * returns them as STRINGS (`dateStrings: true`); parsing one into a Date reinterprets it in the
 * VIEWER's timezone and can move a birthday or an appointment to the day before. Everything here
 * reads the digits textually. "Today" is always passed in, resolved by the caller in the site's
 * timezone.
 */

/**
 * Open Dental writes "no date" as a real value, not NULL: 0001-01-01 (and, on older rows,
 * 1900-01-01 / 0000-00-00). Treating one as a date shows a patient born in year 1, aged 2025.
 */
export function isOdDate(value) {
  if (typeof value !== 'string') return false;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (y < 1901 || mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  return true;
}

function parts(value) {
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return { y: m[1], mo: m[2], d: m[3], h: m[4], mi: m[5] };
}

/**
 * "09/25/2026" (or "25/09/2026" for an office that writes day first), or '' for no date.
 * `format` is 'MM/DD/YYYY' | 'DD/MM/YYYY' — the caller resolves it from the site timezone the same
 * way the rest of Teamio does (utils/taskDraft.js dateFormatFor).
 */
export function formatOdDate(value, format) {
  if (!isOdDate(value)) return '';
  const p = parts(value);
  return format === 'DD/MM/YYYY' ? `${p.d}/${p.mo}/${p.y}` : `${p.mo}/${p.d}/${p.y}`;
}

/** "09/25/2026 · 2:05 pm", or just the date when there is no time of day on the value. */
export function formatOdDateTime(value, format) {
  const date = formatOdDate(value, format);
  if (!date) return '';
  const p = parts(value);
  if (p.h === undefined) return date;
  const h24 = Number(p.h);
  // Midnight exactly is how a DATE column arrives through a DATETIME alias — not a real time.
  if (h24 === 0 && Number(p.mi) === 0) return date;
  const suffix = h24 < 12 ? 'am' : 'pm';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${date} · ${h12}:${p.mi} ${suffix}`;
}

/**
 * Whole years between a birthdate and `today` (both YYYY-MM-DD), or null when either is missing or
 * the result is implausible — the same 0–119 range Patient Profile's own SQL uses for "Not Entered".
 */
export function ageOn(birthdate, today) {
  if (!isOdDate(birthdate) || !isOdDate(today)) return null;
  const b = parts(birthdate);
  const t = parts(today);
  let age = Number(t.y) - Number(b.y);
  if (`${t.mo}${t.d}` < `${b.mo}${b.d}`) age -= 1;
  if (age < 0 || age >= 120) return null;
  return age;
}

/** What the panel calls the patient: preferred name first when the office recorded one. */
export function patientFullName(patient, preferred) {
  const p = patient || {};
  const first = preferred || p.Preferred || p.FName || '';
  const last = p.LName || '';
  return `${first} ${last}`.trim() || 'Unknown patient';
}

/** Two letters for the avatar. */
export function patientInitials(patient, preferred) {
  const p = patient || {};
  const first = String(preferred || p.Preferred || p.FName || '').trim();
  const last = String(p.LName || '').trim();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
}

/**
 * Open Dental's commlog.Mode_, with its real names.
 *
 * ⚠️ Deliberately NOT Patient Profile's badges, which label Mail (2), In person (4) and Email &
 * text (6) all as "Email". These are OD's own values, documented in the query itself
 * (main-service/sql/patient-profile.js, the commlog comment).
 */
export const COMMLOG_MODES = {
  0: { key: 'other', label: 'Note' },
  1: { key: 'email', label: 'Email' },
  2: { key: 'other', label: 'Mail' },
  3: { key: 'phone', label: 'Phone' },
  4: { key: 'other', label: 'In person' },
  5: { key: 'text', label: 'Text' },
  6: { key: 'email', label: 'Email & text' },
};

export function commlogMode(mode) {
  return COMMLOG_MODES[Number(mode)] || { key: 'other', label: 'Other' };
}

/**
 * The filter chips on the Communication tab, in order — design 1a, which is the classic Patient
 * Profile's own radio set (All, Teamio, Email, Text, Mail, Phone, In Person, Email And Text), plus
 * "Other" for mode 0 / unknown modes so no row is reachable only through "All".
 *
 * Every chip but All and Teamio owns a set of OD `Mode_` values, and together they partition every
 * mode. Teamio is a CROSS-CUT (any mode whose note starts "[Teamio]" — the classic page's mode 100).
 */
export const COMMLOG_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'teamio', label: 'Teamio' },
  { key: 'email', label: 'Email', modes: [1] },
  { key: 'text', label: 'Text', modes: [5] },
  { key: 'mail', label: 'Mail', modes: [2] },
  { key: 'phone', label: 'Phone', modes: [3] },
  { key: 'inperson', label: 'In Person', modes: [4] },
  { key: 'emailtext', label: 'Email And Text', modes: [6] },
  { key: 'other', label: 'Other' },
];

// Literal "[Teamio]" at the start. (It used to be /^[Teamio]/, a CHARACTER CLASS: any note starting with T, e, a, m, i
// or o - "Talked with patient", "Called ..." - was labelled Outgoing and listed under the Teamio chip.)
const TEAMIO_NOTE_RE = /^\[Teamio\]/;
const OWNED_MODES = COMMLOG_FILTERS.reduce((all, f) => all.concat(f.modes || []), []);

/** Was this commlog entry written by Teamio (a text, email or form we sent)? */
export function isTeamioCommlog(row) {
  return !!row && TEAMIO_NOTE_RE.test(String(row.Note || ''));
}

/**
 * "Outgoing" for what Teamio sent, '' otherwise. PATIENT_PROFILE does not select OD's
 * SentOrReceived, so nothing else can honestly be called incoming or outgoing.
 */
export function commlogDirection(row) {
  return isTeamioCommlog(row) ? 'Outgoing' : '';
}

export function filterCommlog(rows, key) {
  const list = Array.isArray(rows) ? rows : [];
  if (!key || key === 'all') return list;
  if (key === 'teamio') return list.filter(isTeamioCommlog);
  const chip = COMMLOG_FILTERS.find(f => f.key === key);
  if (!chip) return list;
  if (key === 'other') return list.filter(r => OWNED_MODES.indexOf(Number(r && r.Mode_)) === -1);
  return list.filter(r => (chip.modes || []).indexOf(Number(r && r.Mode_)) !== -1);
}

/**
 * The due pill on a follow-up row (design 1a), from the upcomingFollowups columns:
 *   no FollowupDate      → 'stop'  "Do not follow-up" (a stopped / don't-follow-up status)
 *   pastDue === 'true'   → 'past'  the date, red
 *   dueToday === 'true'  → 'today' "Due today"
 *   otherwise            → 'date'  the date
 */
export function followupDue(row, format) {
  const r = row || {};
  if (!r.FollowupDate) return { kind: 'stop', label: 'Do not follow-up' };
  if (String(r.dueToday) === 'true') return { kind: 'today', label: 'Due today' };
  const label = formatOdDate(r.FollowupDate, format) || String(r.FollowupDate).slice(0, 10);
  return { kind: String(r.pastDue) === 'true' ? 'past' : 'date', label };
}

/** The patient's phone numbers, labelled, blanks dropped, wireless first (the one texts go to). */
export function phoneLines(patient) {
  const p = patient || {};
  return [
    { label: 'Mobile', value: p.WirelessPhone },
    { label: 'Home', value: p.HomePhone },
    { label: 'Work', value: p.WorkPhone },
  ].filter(l => typeof l.value === 'string' && l.value.trim() !== '');
}

// "Can we text this patient?" is NOT answered here — it is src/utils/textConsent.js
// getTextBlockReason(), the one rule every send surface shares (STOP keyword, TxtMsgOk = 2).

/** "$1,234.50" — or "$0.00". Negative amounts are credits: "-$20.00". */
export function money(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '$0.00';
  const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${n < 0 ? '-' : ''}$${abs}`;
}

/** A procedure's tooth and surface, as OD writes them: "#19 MOD", "#19", or ''. */
export function toothLabel(row) {
  const r = row || {};
  const tooth = r.ToothNum ? `#${r.ToothNum}` : '';
  const surf = r.Surf ? String(r.Surf) : '';
  return [tooth, surf].filter(Boolean).join(' ');
}

/** Split OD's ProcDescript ("BU, PFM") into its items for a list; '' → []. */
export function procedureItems(procDescript) {
  if (typeof procDescript !== 'string') return [];
  return procDescript.split(',').map(s => s.trim()).filter(Boolean);
}

/**
 * The panel's patient in Patient Profile's `patientData` shape, for the modals Phase 8 reuses
 * (Send Form/Text/Email, Form QR, Collect Payment, Review Request).
 *
 * The endpoint's allow-list drops main-service's CONCAT'd `PatientName`, but three of those modals
 * read it — Collect Payment SENDS it (`patient_name`) — so it is rebuilt exactly as
 * sql/patient-profile.js builds it: `concat(p.LName,', ',p.FName)`. Built from the fields as the
 * panel holds them, so under the demo mask it is the masked name, like everything else.
 * A copy: the modals never write into the store's object.
 */
export function profilePatientData(patient) {
  if (!patient) return null;
  // MySQL CONCAT is NULL when any part is NULL — so is this.
  const name = patient.LName == null || patient.FName == null ? null : `${patient.LName}, ${patient.FName}`;
  return { ...patient, PatientName: name };
}
